import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  IconButton,
  InputAdornment,
  Divider,
  Box,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LaunchIcon from "@mui/icons-material/Launch";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import { RateField } from "../../../../../Components/RateField";
import { useMutation, useQueryClient } from "react-query";
import API from "../../../../../API";

/* helpers */
const money = (v) =>
  parseFloat(String(v ?? "0").replace(/[^0-9.,-]/g, "").replace(/,/g, "")) || 0;
const updVideo = (arr, id, k, v) =>
  arr.map((vid) => (vid.id === id ? { ...vid, [k]: v } : vid));

// Constants matching AppShell
const BAR_HEIGHT = 100;
const EXTRA_GUTTER = 17;

/* ---------- history sub-dialog ---------- */
function HistoryDialog({ open, onClose, loading, entries }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const LABELS = {
    price: "Price",
    status: "Status",
    agencyRate: "Agency Rate",
    assetsSent: "Deliverables Sent",
    assetsApproved: "Deliverables Approved",
    creatorBrief: "Creator Brief",
    postingInstructions: "Posting Instructions",
    skipPayout: "Skip Payment",
    payoutComplete: "Payment Completed",
    boostCode: "Boost Code",
    name: "Creator Name",
    submissionLink: "Submission Link",
  };

  const nice = (k, v) => {
    if (v == null || v === "") return "(empty)";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (["price", "agencyRate"].includes(k) && !isNaN(v))
      return `$${parseFloat(v).toFixed(2)}`;
    return v.toString();
  };

  const listLines = (changesRaw) => {
    const ch =
      typeof changesRaw === "string"
        ? JSON.parse(changesRaw || "{}")
        : changesRaw || {};
    const rows = [];
    Object.values(ch).forEach((sect) =>
      Object.entries(sect || {}).forEach(([path, diff]) => {
        const k = path.match(/\['([^']+)'\]$/)?.[1] || path;
        if (!LABELS[k]) return;
        rows.push(
          `${LABELS[k]}: ${nice(k, diff.old_value)} → ${nice(k, diff.new_value)}`
        );
      })
    );
    return rows.length ? rows : ["(no tracked field changes)"];
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          ...(isMobile && {
            mb: `calc(${BAR_HEIGHT + EXTRA_GUTTER}px + env(safe-area-inset-bottom, 0px))`,
          }),
        },
      }}
    >
      <DialogTitle>Creator History</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : entries.length === 0 ? (
          <Typography>No history entries.</Typography>
        ) : (
          entries.map((h) => (
            <Box key={h.id} mb={3}>
              <Typography variant="subtitle2">
                {new Date(h.timestamp).toLocaleString()} — {h.user_name}
              </Typography>
              <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                {listLines(h.changes).map((ln, i) => (
                  <li key={i}>{ln}</li>
                ))}
              </ul>
              <Divider sx={{ mt: 2 }} />
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------- main dialog ---------- */
export default function CampaignCreatorEditDialog({
  open,
  onClose,
  creator,
  campaignId,
  index,
  handleUploadClick,
  onFieldChange, // update parent immediately for UI
  onDropCreator,
  creatorId,
  isTcc = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const qc = useQueryClient();

  const [draft, setDraft] = useState({ ...creator });
  const [dirty, setDirty] = useState(new Set());

  /* history */
  const [hOpen, setHOpen] = useState(false);
  const [hLoading, setHLoading] = useState(false);
  const [entries, setEntries] = useState([]);

  /* toast */
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });

  /* sync when reopened */
  useEffect(() => {
    if (open) {
      setDraft({ ...creator });
      setDirty(new Set());
    }
  }, [open, creator]);

  const flag = (k) => setDirty((s) => new Set(s).add(k));
  const setField = (k, v) => {
    flag(k);
    setDraft((d) => ({ ...d, [k]: v }));
  };
  const setVideo = (id, k, v) => {
    flag("videos");
    setDraft((d) => ({ ...d, videos: updVideo(d.videos ?? [], id, k, v) }));
  };

  /* mutation – saves just this creator */
  const { mutateAsync: saveCreator, isLoading } = useMutation(
    (payload) => API.campaigns.updateCampaignCreatorSpecs(payload),
    {
      onSuccess: () => {
        qc.invalidateQueries(["campaign", campaignId]);
        setToast({ open: true, sev: "success", msg: "Saved!" });
      },
      onError: (e) =>
        setToast({
          open: true,
          sev: "error",
          msg: e.response?.data?.error || e.message,
        }),
    }
  );

  /* save handler */
  const handleSave = async () => {
    if (dirty.size === 0) return onClose();

    const patch = {};
    dirty.forEach((k) => {
      patch[k] =
        ["price", "agencyRate"].includes(k) ? money(draft[k]) : draft[k];
    });
    if (patch.assetsApproved) patch.status = "Approved";

    /* backend save */
    await saveCreator({
      campaignId,
      creators: [{ id: creator.id, ...patch }],
    });

    /* update parent state WITHOUT triggering Unsaved flag */
    Object.entries(patch).forEach(([k, v]) =>
      onFieldChange(k, v, false /* no unsaved */)
    );

    onClose();
  };

  /* history fetch */
  const openHistory = async () => {
    if (hOpen) return setHOpen(true);
    setHLoading(true);
    setHOpen(true);
    try {
      const res = await API.campaigns.creatorHistory(campaignId, creatorId);
      setEntries(res || []);
    } catch (err) {
      setToast({
        open: true,
        sev: "error",
        msg: err.response?.data?.error || "Failed to load history",
      });
    } finally {
      setHLoading(false);
    }
  };

  const parentVids = (draft.videos ?? []).filter((v) => !v.parentId);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          ...(isMobile && {
            mb: `calc(${BAR_HEIGHT + EXTRA_GUTTER}px + env(safe-area-inset-bottom, 0px))`,
          }),
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Edit Creator
        <IconButton size="small" onClick={openHistory}>
          <HistoryIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* MAIN FORM */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RateField
              label="Price"
              value={draft.price}
              fullWidth
              onChange={(v) => setField("price", v)}
            />
          </Grid>
          {isTcc && (
            <Grid item xs={12} md={6}>
              <RateField
                label="Agency Rate"
                value={draft.agencyRate}
                fullWidth
                onChange={(v) => setField("agencyRate", v)}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!draft.assetsSent}
                  onChange={(e) => setField("assetsSent", e.target.checked)}
                />
              }
              label="Deliverables Sent"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!draft.assetsApproved}
                  disabled={!draft.assetsSent}
                  onChange={(e) =>
                    setField("assetsApproved", e.target.checked)
                  }
                />
              }
              label="Approved"
              sx={{ ml: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!draft.skipPayout}
                  onChange={(e) => setField("skipPayout", e.target.checked)}
                />
              }
              label="Skip Payment"
              sx={{ ml: 2 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Creator Brief"
              value={draft.creatorBrief ?? ""}
              multiline
              minRows={3}
              fullWidth
              onChange={(e) => setField("creatorBrief", e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleUploadClick(index)}>
                      <CloudUploadIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {draft.submissionLink && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<LaunchIcon />}
                onClick={() =>
                  window.open(draft.submissionLink, "_blank", "noopener")
                }
              >
                Open Submission Folder
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Posting Instructions"
              value={draft.postingInstructions ?? ""}
              multiline
              minRows={3}
              fullWidth
              onChange={(e) =>
                setField("postingInstructions", e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mt: 3, mb: 1 }}>Video Links & Boost Codes</Divider>
            {parentVids.map((v) => (
              <Box key={v.id} mb={2}>
                <Typography variant="subtitle2">Video #{v.id}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Live Link"
                      value={v.liveLink ?? ""}
                      onChange={(e) => setVideo(v.id, "liveLink", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Boost Code"
                      value={v.boostCode ?? ""}
                      onChange={(e) => setVideo(v.id, "boostCode", e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <IconButton color="error" onClick={onDropCreator}>
          <DeleteIcon />
        </IconButton>
        <Box flexGrow={1} />
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading || dirty.size === 0}
        >
          {isLoading ? "Saving…" : "Save Changes"}
        </Button>
      </DialogActions>

      {/* History & Toast */}
      <HistoryDialog
        open={hOpen}
        onClose={() => setHOpen(false)}
        loading={hLoading}
        entries={entries}
      />
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.sev}
          sx={{ width: "100%" }}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}